/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {forwardRef} from 'react'
import {useButton} from 'react-aria'

import Box from '@fuegokit/react/components/Box'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import {ForwardRefComponent as PolymorphicForwardRefComponent} from '@fuegokit/react/utils/polymorphic'
import {ButtonBaseProps} from './types'
import {StyledButton} from './styles'

const ButtonBase = forwardRef(
  (
    {
      children,
      as: Component = 'button',
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      UNSAFE_className,
      ...props
    }: ButtonBaseProps,
    forwardedRef
  ): JSX.Element => {
    const {href, onPress} = props
    if (href && onPress) {
      console.error(
        `Warning: Use an href with buttons that should render as semantic HTML <a> tags and an onPress handler with buttons that should render as semantic <button> tags, but not both.`
      )
    }
    const ariaButtonRef = props.buttonRef
    const innerRef = React.useRef<HTMLButtonElement>(null)
    const {buttonProps} = useButton(props, innerRef)
    if (ariaButtonRef) {
      useRefObjectAsForwardedRef(ariaButtonRef, innerRef)
    } else {
      useRefObjectAsForwardedRef(forwardedRef, innerRef)
    }

    // we need to remove margin in cases where consumers are only
    // passing a single icon to buttons
    const {isOnlySingleIcon, ariaLabel} = props

    const iconWrapStyles = {
      display: 'inline-block',
      flexGrow: 0,
      flexShrink: 0,
      alignSelf: 'center',
      fontSize: 0,
      lineHeight: 0
    }
    const trailingIconStyles = {
      ...iconWrapStyles,
      ml: isOnlySingleIcon ? '' : 1
    }

    return (
      <StyledButton
        as={Component}
        aria-label={ariaLabel}
        ref={innerRef}
        className={UNSAFE_className}
        href={Component === 'button' ? undefined : href}
        onClick={Component === 'a' ? undefined : onPress}
        {...buttonProps}
        {...props}
      >
        {LeadingIcon && (
          <Box as="span" data-component="leadingIcon" sx={iconWrapStyles}>
            <LeadingIcon />
          </Box>
        )}
        {children && <span data-component="text">{children}</span>}
        {TrailingIcon && (
          <Box as="span" data-component="trailingIcon" sx={trailingIconStyles}>
            <TrailingIcon />
          </Box>
        )}
      </StyledButton>
    )
  }
) as PolymorphicForwardRefComponent<'button' | 'a', ButtonBaseProps>

ButtonBase.displayName = 'ButtonBase'
export {ButtonBase}
